"use client";

import React, { FC, useState } from "react";
import { Challenge } from "@/lib/challengePaths";
import { ChallengeQuestionContent } from "./ChallengeQuestionContent";

interface Props {
  challenge: Challenge;
  onComplete: (isCorrect: boolean, points: number) => void;
}

export const ChallengePlayer: FC<Props> = ({ challenge, onComplete }) => {
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [estimateInput, setEstimateInput] = useState("");

  const handleChoiceAnswer = (choiceIndex: number) => {
    const correct = challenge.correctAnswer.includes(choiceIndex);
    setIsCorrect(correct);
    setShowResult(true);
  };

  const handleEstimateSubmit = () => {
    const userAnswer = parseFloat(estimateInput);
    if (isNaN(userAnswer)) {
      alert("Please enter a valid number");
      return;
    }

    let correct = false;

    if (challenge.acceptableRange && Array.isArray(challenge.acceptableRange)) {
      const [min, max] = challenge.acceptableRange;
      correct = userAnswer >= min && userAnswer <= max;
    } else if (Array.isArray(challenge.correctAnswer)) {
      const [min, max] = challenge.correctAnswer;
      correct = userAnswer >= min && userAnswer <= max;
    }
    setIsCorrect(correct);
    setShowResult(true);
  };

  const handleNext = () => {
    onComplete(isCorrect, isCorrect ? challenge.points : 0);
  };

  const handleEstimateInput = (e: React.ChangeEvent<HTMLInputElement>) =>
    setEstimateInput(e.target.value);

  return (
    <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 max-w-2xl w-full mx-auto">
      <h3 className="text-2xl font-bold">{challenge.title}</h3>
      <p className="text-gray-400 mt-2 mb-6">{challenge.description}</p>
      {!showResult ? (
        <ChallengeQuestionContent
          challenge={challenge}
          estimateInput={estimateInput}
          handleChoiceAnswer={handleChoiceAnswer}
          handleEstimateInput={handleEstimateInput}
          handleEstimateSubmit={handleEstimateSubmit}
        />
      ) : (
        <div className="space-y-4">
          <div
            className={`p-4 rounded-lg ${
              isCorrect
                ? "bg-green-500/20 text-green-300"
                : "bg-red-500/20 text-red-300"
            }`}
          >
            <h4 className="font-bold text-lg">
              {isCorrect ? "Correct!" : "Incorrect"}
            </h4>
            <p className="mt-2">{challenge.explanation}</p>
            {challenge.type === "estimate" && (
              <div className="mt-3 text-sm">
                <p>Your answer: {estimateInput}%</p>
                <p>
                  Acceptable range:{" "}
                  {challenge.acceptableRange
                    ? `${challenge.acceptableRange[0]}-${challenge.acceptableRange[1]}%`
                    : Array.isArray(challenge.correctAnswer)
                    ? `${challenge.correctAnswer[0]}-${challenge.correctAnswer[1]}%`
                    : "See explanation above"}
                </p>
              </div>
            )}
          </div>
          <button
            onClick={handleNext}
            className="w-full bg-brand-teal hover:bg-brand-teal text-white py-3 rounded-xl font-semibold"
          >
            Continue
          </button>
        </div>
      )}
    </div>
  );
};
